import { createContext } from 'react';
import { Dayjs } from 'dayjs';
import { ApplicationLogCategory, ApplicationLogSeverity } from '@/modules/application-log/api/application-log.contracts';
import { EventType } from '@/modules/events/api/events/event.contracts';
import { PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import {
  BatteryStatusSearchParam,
  LinkedTrackerSearchParam,
  TrackerTypeSearchParam,
} from '@/modules/trackers/api/trackers/tracker.contracts';
import { MovementType } from '@/modules/movements/api/movements.contracts';
import { TransactionType, TransactionStatus, TransactionSource } from '@/modules/transactions/api/transactions/transaction.contracts';

export enum FilterFieldName {
  locationId = 'locationId',
  assetTypeId = 'assetTypeId',
  assetId = 'assetId',
  searchText = 'searchText',
  dateFrom = 'dateFrom',
  dateTo = 'dateTo',
  arrivedAtDateFrom = 'arrivedAtDateFrom',
  arrivedAtDateTo = 'arrivedAtDateTo',
  logSeverity = 'logSeverity',
  logCategory = 'logCategory',
  eventType = 'eventType',
  messageId = 'messageId',
  labelValueIds = 'labelValueIds',
  assetTypeIds = 'assetTypeIds',
  pageNumber = 'pageNumber',
  sortOption = 'sortOption',
  sortDirection = 'sortDirection',
  searchNumber = 'searchNumber',
  stayTimeInDays = 'stayTimeInDays',
  trackerType = 'trackerType',
  linkedTracker = 'linkedTracker',
  batteryStatus = 'batteryStatus',
  toLocationId = 'toLocationId',
  fromLocationId = 'fromLocationId',
  transactionType = 'transactionType',
  transactionStatus = 'transactionStatus',
  transactionSource = 'transactionSource',
  movementType = 'movementType',
}

export interface FilterValues {
  [FilterFieldName.assetId]?: number;
  [FilterFieldName.locationId]?: number;
  [FilterFieldName.assetTypeId]?: number;
  [FilterFieldName.searchText]?: string;
  [FilterFieldName.dateFrom]?: Dayjs;
  [FilterFieldName.dateTo]?: Dayjs;
  [FilterFieldName.arrivedAtDateFrom]?: Dayjs;
  [FilterFieldName.arrivedAtDateTo]?: Dayjs;
  [FilterFieldName.logSeverity]?: ApplicationLogSeverity;
  [FilterFieldName.logCategory]?: ApplicationLogCategory;
  [FilterFieldName.eventType]?: EventType;
  [FilterFieldName.messageId]?: string;
  [FilterFieldName.labelValueIds]?: number[];
  [FilterFieldName.assetTypeIds]?: number[];
  [FilterFieldName.pageNumber]?: number;
  [FilterFieldName.sortOption]?: PageSortOption;
  [FilterFieldName.sortDirection]?: PageSortOrder;
  [FilterFieldName.searchNumber]?: number;
  [FilterFieldName.stayTimeInDays]?: number;
  [FilterFieldName.trackerType]?: TrackerTypeSearchParam | null;
  [FilterFieldName.linkedTracker]?: LinkedTrackerSearchParam | null;
  [FilterFieldName.batteryStatus]?: BatteryStatusSearchParam | null;
  [FilterFieldName.toLocationId]?: number;
  [FilterFieldName.fromLocationId]?: number;
  [FilterFieldName.movementType]?: MovementType;
  [FilterFieldName.transactionType]?: TransactionType;
  [FilterFieldName.transactionStatus]?: TransactionStatus;
  [FilterFieldName.transactionSource]?: TransactionSource;
}

export type FilterEntry<T> = {
  value: T;
  humanValue: string;
};

export type Filters = {
  [K in keyof FilterValues]?: FilterEntry<FilterValues[K]>;
};

export const isFilterFieldName = (key: string): key is FilterFieldName => {
  return Object.values(FilterFieldName).includes(key as FilterFieldName);
};

interface FilterBarContextProps {
  filterValues: FilterValues;
  setFilterValue: (field: FilterFieldName, value: unknown, humanValue?: string) => void;
  triggerSearch: () => void;
  isFilterActive: (filterName: FilterFieldName) => boolean;
  getFilterValue: (filterName: FilterFieldName) => unknown | undefined;
  getFilterHumanValue: (filterName: FilterFieldName) => string | undefined;
}

// Initialize the context
export const FilterBarContext = createContext<FilterBarContextProps>({
  filterValues: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFilterValue: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  triggerSearch: () => {},
  isFilterActive: () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  getFilterValue: () => undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  getFilterHumanValue: () => undefined,
});
